import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { ImagePlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient, tokenStorage } from '../../utils/api-client';

interface BoardImageUploadProps {
  /** Preview to show before any new upload (the resolved board/type image). */
  currentImageUrl: string | null;
  onUploaded: (signedId: string, previewUrl: string) => void;
  /**
   * Asked before the file picker opens, with the way to open it: for a caller
   * that has something to warn about first (a new photo archives the problems
   * on a board). Absent, the picker opens at once.
   */
  onBeforePick?: (pick: () => void) => void;
  /** The button's words, where "upload image" is not what the act is. */
  label?: string;
}

/**
 * Uploads a board photo via ActiveStorage direct upload and hands back the
 * signed id (sent with the board payload) plus a local preview URL.
 */
export default function BoardImageUpload({ currentImageUrl, onUploaded, onBeforePick, label }: BoardImageUploadProps) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<string | null>(currentImageUrl);
  const pendingPreviewRef = useRef<string | null>(null);

  const getApiOrigin = useCallback(() => {
    const baseURL = apiClient.getBaseURL();
    const source = baseURL || window.location.href;
    try {
      const url = new URL(source);
      return { host: url.hostname, port: url.port || undefined, protocol: url.protocol.replace(':', '') };
    } catch {
      return {
        host: window.location.hostname,
        port: window.location.port || undefined,
        protocol: window.location.protocol.replace(':', ''),
      };
    }
  }, []);

  return (
    <div className="space-y-2">
      {preview && (
        <img src={preview} alt="" className="max-h-48 w-full rounded-md border object-contain" />
      )}
      <DirectUploadProvider
        origin={getApiOrigin()}
        headers={{ Authorization: `Bearer ${tokenStorage.getToken() || ''}` }}
        onSuccess={(signedIds: string[]) => {
          const previewUrl = pendingPreviewRef.current;
          if (signedIds.length > 0 && previewUrl) {
            onUploaded(signedIds[0], previewUrl);
          }
        }}
        render={({ handleUpload, uploads, ready }: {
          handleUpload: (files: FileList) => void;
          uploads: Array<{ state: string }>;
          ready: boolean;
        }) => {
          const uploading = uploads.some((u) => u.state === 'uploading');

          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (files && files.length > 0) {
              const objectUrl = URL.createObjectURL(files[0]);
              pendingPreviewRef.current = objectUrl;
              setPreview(objectUrl);
              handleUpload(files);
            }
            e.target.value = '';
          };

          return (
            <div>
              <input
                id="board-image-input"
                type="file"
                accept="image/*"
                className="hidden"
                disabled={!ready || uploading}
                onChange={handleChange}
              />
              <Button
                type="button"
                variant="outline"
                disabled={!ready || uploading}
                onClick={() => {
                  const pick = () => document.getElementById('board-image-input')?.click();
                  if (onBeforePick) onBeforePick(pick);
                  else pick();
                }}
              >
                {uploading ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : (
                  <ImagePlus className="mr-2 size-4" />
                )}
                {label ?? t('gymAdmin.boards.fields.uploadImage')}
              </Button>
            </div>
          );
        }}
      />
    </div>
  );
}
